import React from 'react';
import { Grid } from '@material-ui/core';
import { useTypedTranslation } from '@app/hooks';
import { PasslyCheckbox } from '@app/components';
import { ICredentialGraphApiVerificationParsedResults, IDomainResult } from '@app/models';

import { DomainResults, GraphApiConfig, ManagedConfig, ManualConfig } from '@app/components/SSOApps/addEditAppTabs/appMsol';
import { Error, VerifiedUser } from '@material-ui/icons';
import { green } from '@material-ui/core/colors';
import { IOffice365State } from '@app/components/SSOApps/addEditAppTabs';
import { MODULE_NAME } from '@app/pages/ssoManager/addEditApplication.enums';

interface IProps {
    state: IOffice365State;
    setState: Function;
    handleOnDisconnect: (module: string) => () => void;
    graphApiVerificationState?: ICredentialGraphApiVerificationParsedResults;
    updateManagedSetup: (name: string, value: unknown) => void;
    verifyCredentials: (module: string) => () => void;
    verifyGraphApiCredentials: (module: string) => () => void;
    connected: boolean;
}

export const AppMsolFederation = ({
    state,
    setState,
    handleOnDisconnect,
    graphApiVerificationState,
    updateManagedSetup,
    verifyCredentials,
    verifyGraphApiCredentials,
    connected
}: IProps) => {
    const { translation } = useTypedTranslation();

    const handleCheckboxChange = (checked: boolean, event: any) => {
        updateManagedSetup(event.target.name, checked);
    };

    const updateBaseConfig = (name: string, value: unknown) => {
        if (
            ((name === 'importMsolUsersEnabled' && !state.importMsolGroupsEnabled) ||
                (name === 'importMsolGroupsEnabled' && !state.importMsolUsersEnabled)) &&
            !value
        ) {
            // both import values are false, set the top level import value to false as well
            let prevState = { ...state };
            prevState[name] = value as boolean;
            prevState.importMsolEnabled = false;
            setState(prevState);
        } else {
            // update first level properties
            setState((prevState: any) => ({ ...prevState, [name]: value }));
        }
    };

    return (
        <>
            <div className='danger'>
                <b>{translation('MSOL_WARNING_LABEL')}</b>:&nbsp;
                <span>{translation('MSOL_DEPRECATION_WARNING')}</span>
            </div>
            <Grid container direction='row'>
                <Grid item md={5}>
                    <ManagedConfig
                        setManagedConfig={updateManagedSetup}
                        config={state.managedSetup}
                        verifyMsol={verifyCredentials(MODULE_NAME.msolModule)}
                        officeConfig={state}
                        updateOfficeConfig={updateBaseConfig}
                    />
                </Grid>
                <Grid item md={7}>
                    <ul className='domain-results'>
                        {state.managedSetup.domainResults.map((result: IDomainResult, index: number) => (
                            <DomainResults result={result} index={index} key={index} />
                        ))}
                    </ul>
                </Grid>
            </Grid>

            <PasslyCheckbox
                name='active'
                className='msol__manage-checkbox'
                checked={state.managedSetup.active}
                onChange={handleCheckboxChange}
                label={translation('MSOL_MANAGED_SERVICE')}
            />

            {!state.managedSetup.active ? <ManualConfig onDisconnect={handleOnDisconnect(MODULE_NAME.msolModule)} connected={connected} domain={state.managedSetup.domain} username={state.managedSetup.username} /> : null}

            <div className='msol__help-text'>{translation('MSOL_COULD_SET_BY_GRAPH_API')}</div>
            <PasslyCheckbox
                name='graphApiEnabled'
                checked={state.managedSetup.graphApiEnabled}
                onChange={handleCheckboxChange}
                label={translation('MSOL_USE_GRAPH_API')}
            />

            {state.managedSetup.graphApiEnabled ? (
                <>
                    <Grid container direction='row'>
                        <Grid item md={5}>
                            <GraphApiConfig
                                setManagedConfig={updateManagedSetup}
                                officeConfig={state}
                                verifyCredentials={verifyGraphApiCredentials(MODULE_NAME.msolModule)}
                            />
                        </Grid>
                        <Grid item md={7}>
                            {graphApiVerificationState && graphApiVerificationState?.isVerified && graphApiVerificationState?.message ? (
                                <VerifiedUser className='msol-icon' htmlColor={green[700]} />
                            ) : graphApiVerificationState && !graphApiVerificationState?.isVerified && graphApiVerificationState?.message ? (
                                <Error className='msol-icon' color='error' />
                            ) : null}
                            <ul className='domain-results'>
                                <span className='domainresults-span'>{graphApiVerificationState?.message}&nbsp;</span>
                            </ul>
                        </Grid>
                    </Grid>
                </>
            ) : null}
        </>
    );
};
